import axios, { AxiosResponse } from 'axios';
import { Storage } from 'react-jhipster';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AppThunk } from 'pages/thermography/config/store';
import { setLocale } from 'pages/thermography/shared/reducers/locale';
import { serializeAxiosError } from './reducer.utils';

const AUTH_TOKEN_KEY = 'jhi-authenticationToken';

export const initialState = {
	loading: false,
	isAuthenticated: false,
	loginSuccess: false,
	loginError: false, // Errors returned from server side
	showModalLogin: false,
	account: {} as any,
	errorMessage: null as unknown as string, // Errors returned from server side
	redirectMessage: null as unknown as string,
	sessionHasBeenFetched: false,
	logoutUrl: null as unknown as string,
};

export type AuthenticationState = Readonly<typeof initialState>;

// Actions

export const getSession = (): AppThunk => async (dispatch, getState) => {
	await dispatch(getAccount());

	const { account } = getState().authentication;
	if (account && account.langKey) {
		const langKey = Storage.session.get('locale', account.langKey);
		await dispatch(setLocale(langKey));
	}
};

export const getAccount = createAsyncThunk('authentication/get_account', async () => axios.get<any>('api/account'), {
	serializeError: serializeAxiosError,
});

interface IAuthParams {
	username: string;
	password: string;
	rememberMe?: boolean;
}

export const authenticate = createAsyncThunk(
	'authentication/login',
	async (auth: IAuthParams) => axios.post<any>('api/authenticate', auth),
	{
		serializeError: serializeAxiosError,
	},
);

export const login: (username: string, password: string, rememberMe?: boolean) => AppThunk =
	(username, password, rememberMe = false) =>
		async dispatch => {
			const result = await dispatch(authenticate({ username, password, rememberMe }));

			const response = result.payload as AxiosResponse;

			const bearerToken = response?.headers?.authorization;
			if (bearerToken && bearerToken.slice(0, 7) === 'Bearer ') {
				const jwt = bearerToken.slice(7, bearerToken.length);
				if (rememberMe) {
					Storage.local.set(AUTH_TOKEN_KEY, jwt);
				} else {
					Storage.session.set(AUTH_TOKEN_KEY, jwt);
				}
			}
			dispatch(getSession());
		};

export const clearAuthToken = () => {
	if (Storage.local.get(AUTH_TOKEN_KEY)) {
		Storage.local.remove(AUTH_TOKEN_KEY);
	}
	if (Storage.session.get(AUTH_TOKEN_KEY)) {
		Storage.session.remove(AUTH_TOKEN_KEY);
	}
};

export const logout: () => AppThunk = () => dispatch => {
	clearAuthToken();
	dispatch(logoutSession());
};

export const clearAuthentication = (messageKey: any) => (dispatch: (arg0: { payload: any; type: "authentication/authError" | "authentication/clearAuth"; }) => void) => {
	clearAuthToken();
	dispatch(authError(messageKey));
	dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
	name: 'authentication',
	initialState: initialState as AuthenticationState,
	reducers: {
		logoutSession() {
			return {
				...initialState,
				showModalLogin: true,
				sessionHasBeenFetched: false, // False porque não buscou sessão do backend após logout
			};
		},
		authError(state, action) {
			return {
				...state,
				showModalLogin: true,
				redirectMessage: action.payload,
			};
		},
		clearAuth(state) {
			return {
				...state,
				loading: false,
				showModalLogin: true,
				isAuthenticated: false,
			};
		},
	},
	extraReducers(builder) {
		builder
			.addCase(authenticate.rejected, (state, action) => {
				console.log('[🟥 authenticate.rejected]', action);
				return {
					...initialState,
					errorMessage: action.error.message || '',
					showModalLogin: true,
					loginError: true,
					sessionHasBeenFetched: false, // False porque falhou ao autenticar
				};
			})
			.addCase(authenticate.fulfilled, (state, action) => {
				console.log('[✅ authenticate.fulfilled]', action);
				return {
					...state,
					loading: false,
					loginError: false,
					showModalLogin: false,
					loginSuccess: true,
				};
			})
			.addCase(authenticate.pending, state => {
				console.log('[⏳ authenticate.pending]');
				state.loading = true;
			})
			.addCase(getAccount.pending, state => {
				console.log('[⏳ getAccount.pending]');
				state.loading = true;
			})
			.addCase(getAccount.rejected, (state, action) => {
				console.log('[🟥 getAccount.rejected]', action);
				return {
					...state,
					loading: false,
					isAuthenticated: false,
					sessionHasBeenFetched: true,
					showModalLogin: true,
					errorMessage: action.error.message || '',
				};
			})
			.addCase(getAccount.fulfilled, (state, action) => {
				console.log('[✅ getAccount.fulfilled]', action);
				const isAuthenticated = action.payload?.data?.activated ?? false;
				return {
					...state,
					isAuthenticated,
					loading: false,
					sessionHasBeenFetched: true,
					account: action.payload.data,
				};
			});
	}

});

export const { logoutSession, authError, clearAuth } = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
